import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/utils/errorFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Manager Override dialog — asks for a manager PIN (and optionally a
 * reason) before either cancelling an in-progress ticket outright or
 * unlocking it for edit. Extracted from POSPage.jsx (~130 lines
 * inline). All side effects (order cancel, table refresh, ticket
 * panel takeover) flow back to the parent via ``onSuccess`` so the
 * component stays presentational.
 */
const ManagerOverrideDialog = ({
  managerAction,
  onClose,
  managerPin, setManagerPin,
  managerReason, setManagerReason,
  managerLoading, setManagerLoading,
  onCancelSuccess,   // (orderId, order) => void — refresh tables, wipe local ticket, refetch orders
  onEditUnlock,      // (order) => void — populate ticket panel for editing
}) => {
  const submit = async () => {
    if (!managerAction) return;
    setManagerLoading(true);
    try {
      if (managerAction.mode === "delete") {
        await axios.post(`${API}/orders/${managerAction.orderId}/cancel`, {
          manager_pin: managerPin,
          reason: managerReason,
        });
        toast.success("Order cancelled");
        onCancelSuccess(managerAction.orderId, managerAction.order);
        onClose();
      } else {
        // edit mode -> verify PIN, then open the ticket panel for editing
        await axios.post(`${API}/auth/verify-manager-pin`, { pin: managerPin });
        onEditUnlock(managerAction.order);
        toast.success("Manager override granted — modify ticket as needed");
        onClose();
      }
    } catch (e) {
      toast.error(formatApiError(e, "Action failed"));
    } finally {
      setManagerLoading(false);
    }
  };

  return (
    <Dialog open={!!managerAction} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[420px]" data-testid="manager-override-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {managerAction?.mode === "delete" ? "Delete Order" : "Modify Order"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-secondary/50 rounded-xl p-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order</span>
              <span className="font-medium">{managerAction?.order?.table_name || "Quick Order"}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-muted-foreground">Total</span>
              <span className="font-mono font-medium">${(managerAction?.order?.total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-muted-foreground">Status</span>
              <span className="capitalize">{managerAction?.order?.status?.replace("_", " ")}</span>
            </div>
          </div>
          {managerAction?.mode === "delete" && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive">
              This will <strong>cancel</strong> the order, free its table, and remove it from the kitchen queue. This action cannot be undone.
            </div>
          )}
          <div className="space-y-2">
            <Label>Manager PIN (required)</Label>
            <Input
              type="password"
              maxLength={4}
              placeholder="****"
              value={managerPin}
              onChange={(e) => setManagerPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="font-mono text-center text-xl tracking-[8px]"
              autoFocus
              data-testid="manager-override-pin"
            />
          </div>
          {managerAction?.mode === "delete" && (
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={managerReason}
                onChange={(e) => setManagerReason(e.target.value)}
                placeholder="e.g. Walkout, wrong order..."
                data-testid="manager-override-reason"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant={managerAction?.mode === "delete" ? "destructive" : "default"}
            disabled={managerLoading || managerPin.length !== 4}
            onClick={submit}
            data-testid="manager-override-confirm"
          >
            {managerLoading ? "Processing..." : managerAction?.mode === "delete" ? "Cancel Order" : "Unlock for Edit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManagerOverrideDialog;
